import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CContainer, CButton } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilArrowLeft, cilCamera } from "@coreui/icons";
import { toast } from "react-toastify";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ru from "date-fns/locale/ru";
import dayjs from "dayjs";

import FilterPanel from "./components/FilterPanel";
import ProofGallery from "./components/ProofGallery";
import PhotoModal from "./components/PhotoModal";
import i18n from "../../../i18n";

registerLocale("ru", ru);

const TruckProofsPage = () => {
  const [loading, setLoading] = useState(false);
  const [proofs, setProofs] = useState([]);

  const [startDate, setStartDate] = useState(dayjs().startOf("day").toDate());
  const [endDate, setEndDate] = useState(dayjs().endOf("day").toDate());
  const [devices, setDevices] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [selectedTruck, setSelectedTruck] = useState("");
  const [availableDates, setAvailableDates] = useState(null);

  const [photoModal, setPhotoModal] = useState({
    visible: false,
    url: "",
    title: "",
  });

  const fetchProofs = useCallback(async (start, end, devId, trId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (start) params.append("start_date", start.toISOString());
      if (end) params.append("end_date", end.toISOString());
      if (devId) params.append("device_id", devId);
      if (trId) params.append("truck_id", trId);

      const res = await axios.get(
        `/api/analytics/truck-proofs/?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setProofs(res.data);
    } catch (err) {
      toast.error("toast.error(i18n.t('truck_proofs_page.photo_load_error', 'Ошибка загрузки фотографий'))");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAvailableDates = useCallback(async (devId, trId) => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (devId) params.append("device_id", devId);
      if (trId) params.append("truck_id", trId);

      const res = await axios.get(
        `/api/analytics/truck-proofs-dates/?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableDates(res.data);
    } catch (err) {
      console.error("console.error(i18n.t('truck_proofs_page.available_dates_load_error', 'Ошибка загрузки доступных дат'), err)", err);
      setAvailableDates([]); 
    }
  }, []);

  const fetchFilters = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [resDev, resTrucks] = await Promise.all([
        axios.get("/api/devices/", { headers }),
        axios.get("/api/trucks/", { headers }),
      ]);
      setDevices(resDev.data.results || resDev.data);
      setTrucks(resTrucks.data);
    } catch (e) {
      toast.error("toast.error(i18n.t('truck_proofs_page.filters_load_error', 'Ошибка загрузки фильтров'))");
    }
  }, []);

  useEffect(() => {
    fetchFilters();
    fetchProofs(startDate, endDate, selectedDevice, selectedTruck);
  }, [fetchFilters, fetchProofs]);

  useEffect(() => {
    fetchAvailableDates(selectedDevice, selectedTruck);
  }, [fetchAvailableDates, selectedDevice, selectedTruck])

  const handleApplyFilters = () => {
    fetchProofs(startDate, endDate, selectedDevice, selectedTruck);
  };

  const openPhotoModal = ({ url, title }) => {
    setPhotoModal({ visible: true, url, title });
  };

  const closePhotoModal = () => {
    setPhotoModal({ visible: false, url: "", title: "" });
  };

  return (
    <div className="bg-light min-vh-100 p-3 p-md-4">
      <CContainer fluid>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
          <h2 className="d-flex align-items-center">
            <CIcon icon={cilCamera} className="me-2 text-primary" size="xl" />
            {i18n.t('truck_proofs_page.photo_control_title', 'Фотоконтроль загрузки')}
          </h2>
          <CButton color="secondary" as={Link} to="/dashboard">
            <CIcon icon={cilArrowLeft} className="me-2" /> {i18n.t('truck_proofs_page.back', 'Назад')}
          </CButton>
        </div>

        <FilterPanel
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          devices={devices}
          selectedDevice={selectedDevice}
          setSelectedDevice={setSelectedDevice}
          trucks={trucks}
          selectedTruck={selectedTruck}
          setSelectedTruck={setSelectedTruck}
          onApply={handleApplyFilters}
          loading={loading}
          availableDates={availableDates}
        />

        <ProofGallery
          proofs={proofs}
          loading={loading}
          onPhotoClick={openPhotoModal}
        />

        <PhotoModal
          visible={photoModal.visible}
          url={photoModal.url}
          title={photoModal.title}
          onClose={closePhotoModal}
        />
      </CContainer>
    </div>
  );
};

export default TruckProofsPage;